import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  Stack,
  Chip,
  Alert,
  Card,
  CardActionArea,
  CardContent,
  TablePagination,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import { Download, Refresh, Search } from "@mui/icons-material";
import { auditAPI } from "../services/api";
import { format } from "date-fns";
import AuditLogDetailDialog from "../components/AuditLogDetailDialog/AuditLogDetailDialog";

const ACTIONS = [
  "ALL",
  "CREATE",
  "UPDATE",
  "DELETE",
  "VIEW",
  "DOWNLOAD",
  "UPLOAD",
  "EXPORT",
  "IMPORT",
  "LOGIN",
  "LOGOUT",
  "REGISTER",
  "PASSWORD_RESET",
  "ASSIGN_ROLE",
  "REMOVE_ROLE",
  "UPDATE_PERMISSIONS",
  "GRANT_ACCESS",
  "REVOKE_ACCESS",
  "EMAIL_SEND",
];

const ENTITY_TYPES = [
  "ALL",
  "COMPANY",
  "PERSON",
  "OWNERSHIP",
  "DOCUMENT",
  "COMPLIANCE",
  "USER",
  "SYSTEM",
  "EMAIL",
  "INVITATION",
  "REPORT",
];

interface AuditLog {
  id: string;
  userEmail: string;
  action: string;
  entityType: string;
  entityName?: string;
  entityId?: string;
  success: boolean;
  ipAddress?: string;
  createdAt: string;
  description?: string;
  endpoint?: string;
  httpMethod?: string;
}

const AuditLogs: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAction, setSelectedAction] = useState("ALL");
  const [selectedEntityType, setSelectedEntityType] = useState("ALL");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [successOnly, setSuccessOnly] = useState<boolean | undefined>(
    undefined,
  );

  // Detail dialog
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {
        page: page + 1,
        limit: pageSize,
      };

      if (searchTerm) params.search = searchTerm;
      if (selectedAction !== "ALL") params.action = selectedAction;
      if (selectedEntityType !== "ALL") params.entityType = selectedEntityType;
      if (startDate) params.startDate = startDate.toISOString();
      if (endDate) params.endDate = endDate.toISOString();
      if (successOnly !== undefined) params.successOnly = successOnly;

      const response = await auditAPI.getAll(params);
      setLogs(response.data.data || []);
      setTotal(response.data.meta?.total || 0);
    } catch (err: any) {
      console.error("Error fetching audit logs:", err);
      setError(err.response?.data?.message || "Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  }, [
    page,
    pageSize,
    searchTerm,
    selectedAction,
    selectedEntityType,
    startDate,
    endDate,
    successOnly,
  ]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleSearch = () => {
    setPage(0);
    fetchLogs();
  };

  const handleReset = () => {
    setSearchTerm("");
    setSelectedAction("ALL");
    setSelectedEntityType("ALL");
    setStartDate(null);
    setEndDate(null);
    setSuccessOnly(undefined);
    setPage(0);
    fetchLogs();
  };

  const handleExport = async () => {
    try {
      const params: any = {};
      if (searchTerm) params.search = searchTerm;
      if (selectedAction !== "ALL") params.action = selectedAction;
      if (selectedEntityType !== "ALL") params.entityType = selectedEntityType;
      if (startDate) params.startDate = startDate.toISOString();
      if (endDate) params.endDate = endDate.toISOString();
      if (successOnly !== undefined) params.successOnly = successOnly;

      const response = await auditAPI.export(params);
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `audit-logs-${format(new Date(), "yyyy-MM-dd")}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error("Error exporting logs:", err);
      setError("Failed to export audit logs");
    }
  };

  const columns: GridColDef[] = [
    {
      field: "createdAt",
      headerName: "Timestamp",
      width: 180,
      valueFormatter: (params) => {
        return format(new Date(params.value), "yyyy-MM-dd HH:mm:ss");
      },
    },
    {
      field: "userEmail",
      headerName: "User",
      width: 200,
      valueGetter: (params) => params.row.userEmail || "System",
    },
    {
      field: "action",
      headerName: "Action",
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={
            params.value === "CREATE"
              ? "success"
              : params.value === "DELETE"
                ? "error"
                : params.value === "UPDATE"
                  ? "warning"
                  : params.value === "EMAIL_SEND"
                    ? "info"
                    : "default"
          }
        />
      ),
    },
    {
      field: "entityType",
      headerName: "Entity Type",
      width: 120,
    },
    {
      field: "entityName",
      headerName: "Entity Name",
      width: 200,
      valueGetter: (params) => params.row.entityName || "-",
    },
    {
      field: "success",
      headerName: "Status",
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? "Success" : "Failed"}
          size="small"
          color={params.value ? "success" : "error"}
        />
      ),
    },
    {
      field: "ipAddress",
      headerName: "IP Address",
      width: 130,
      valueGetter: (params) => params.row.ipAddress || "-",
    },
    {
      field: "description",
      headerName: "Description",
      width: 250,
      flex: 1,
      valueGetter: (params) => params.row.description || "-",
    },
  ];

  const getActionColor = (
    action: string,
  ): "success" | "error" | "warning" | "info" | "default" => {
    if (action === "CREATE") return "success";
    if (action === "DELETE") return "error";
    if (action === "UPDATE") return "warning";
    if (action === "EMAIL_SEND") return "info";
    return "default";
  };

  const openLogDetails = (log: AuditLog) => {
    setSelectedLog(log);
    setDetailDialogOpen(true);
  };

  const renderMobileCards = () => (
    <Stack spacing={2}>
      {logs.map((log) => (
        <Card key={log.id} sx={{ borderRadius: 2 }}>
          <CardActionArea onClick={() => openLogDetails(log)}>
            <CardContent>
              <Stack spacing={1.25}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 1,
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600} noWrap>
                      {log.entityName || log.entityType || "Audit Event"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {log.userEmail || "System"}
                    </Typography>
                  </Box>
                  <Chip
                    label={log.success ? "Success" : "Failed"}
                    size="small"
                    color={log.success ? "success" : "error"}
                  />
                </Box>

                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                  <Chip
                    label={log.action}
                    size="small"
                    color={getActionColor(log.action)}
                  />
                  <Chip
                    label={log.entityType}
                    size="small"
                    variant="outlined"
                  />
                  {log.httpMethod && (
                    <Chip
                      label={log.httpMethod}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>

                {log.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {log.description}
                  </Typography>
                )}

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {format(new Date(log.createdAt), "MMM dd, yyyy HH:mm")}
                  </Typography>
                  {log.ipAddress && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {log.ipAddress}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </CardContent>
          </CardActionArea>
        </Card>
      ))}
    </Stack>
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: 2,
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <Typography variant="h4">Audit Logs</Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleExport}
          >
            Export CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchLogs}
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              label="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by user, entity, or description"
              size="small"
              sx={{ flex: 1 }}
            />
            <TextField
              label="Action"
              value={selectedAction}
              onChange={(e) => setSelectedAction(e.target.value)}
              select
              size="small"
              sx={{ minWidth: { xs: "100%", md: 150 } }}
            >
              {ACTIONS.map((action) => (
                <MenuItem key={action} value={action}>
                  {action}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Entity Type"
              value={selectedEntityType}
              onChange={(e) => setSelectedEntityType(e.target.value)}
              select
              size="small"
              sx={{ minWidth: { xs: "100%", md: 150 } }}
            >
              {ENTITY_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate ? format(startDate, "yyyy-MM-dd") : ""}
              onChange={(e) =>
                setStartDate(e.target.value ? new Date(e.target.value) : null)
              }
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ minWidth: { xs: "100%", md: "auto" } }}
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate ? format(endDate, "yyyy-MM-dd") : ""}
              onChange={(e) =>
                setEndDate(e.target.value ? new Date(e.target.value) : null)
              }
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ minWidth: { xs: "100%", md: "auto" } }}
            />
            <TextField
              label="Success Filter"
              value={
                successOnly === undefined
                  ? "ALL"
                  : successOnly
                    ? "SUCCESS"
                    : "FAILED"
              }
              onChange={(e) => {
                const val = e.target.value;
                setSuccessOnly(val === "ALL" ? undefined : val === "SUCCESS");
              }}
              select
              size="small"
              sx={{ minWidth: { xs: "100%", md: 150 } }}
            >
              <MenuItem value="ALL">All</MenuItem>
              <MenuItem value="SUCCESS">Success Only</MenuItem>
              <MenuItem value="FAILED">Failed Only</MenuItem>
            </TextField>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Button
              variant="contained"
              startIcon={<Search />}
              onClick={handleSearch}
            >
              Search
            </Button>
            <Button variant="outlined" onClick={handleReset}>
              Reset Filters
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Data Grid */}
      {isMobile ? (
        <Paper sx={{ p: 2 }}>
          {loading ? (
            <Typography variant="body2" color="text.secondary">
              Loading audit logs…
            </Typography>
          ) : logs.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No audit logs found.
            </Typography>
          ) : (
            renderMobileCards()
          )}
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(event) => {
              setPageSize(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{
              mt: 1,
              ".MuiTablePagination-toolbar": {
                px: 0,
                flexWrap: "wrap",
                justifyContent: "center",
              },
              ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
                {
                  m: 0,
                },
            }}
          />
        </Paper>
      ) : (
        <Paper sx={{ height: 600 }}>
          <DataGrid
            rows={logs}
            columns={columns}
            loading={loading}
            pagination
            paginationMode="server"
            rowCount={total}
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(model) => {
              setPage(model.page);
              setPageSize(model.pageSize);
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            onRowClick={(params) => openLogDetails(params.row as AuditLog)}
            slots={{ toolbar: GridToolbar }}
            sx={{
              "& .MuiDataGrid-row": {
                cursor: "pointer",
              },
            }}
          />
        </Paper>
      )}

      {/* Detail Dialog */}
      <AuditLogDetailDialog
        open={detailDialogOpen}
        onClose={() => {
          setDetailDialogOpen(false);
          setSelectedLog(null);
        }}
        log={selectedLog}
      />
    </Box>
  );
};

export default AuditLogs;
